import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { colors, typography, spacing, media } from '../theme';
import { AgentMode } from '../components/AgentMode/AgentMode';
import { ManipulationTechniques } from '../components/ManipulationTechniques';
import { DomainAnalysis } from '../components/DomainAnalysis';
import { AiTamper } from '../components/AiTamper';
import { ClaimAnalysis } from '../components/ClaimAnalysis';
import { PipelineAnalysis } from '../components/PipelineAnalysis';
import { Settings } from '../components/Settings/Settings';
import { ThemeToggle } from '../components/ThemeToggle';
import { UserProfileWidget } from '../components/UserProfile';
import { Sidebar } from '../components/Sidebar';
import type { SidebarSection } from '../components/Sidebar';
import History from './History';
import { useAuth } from '../contexts/AuthContext';
import { useIsMobile } from '../hooks/useMediaQuery';
import { lockScroll, unlockScroll } from '../utils/scroll-lock';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { logout } = useAuth();
  const [activeSection, setActiveSection] = useState<SidebarSection>('pipeline');
  const [isDetailViewOpen, setIsDetailViewOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isMobile = useIsMobile();

  // Auto-close mobile menu when viewport goes desktop
  useEffect(() => {
    if (!isMobile) {
      setIsMobileMenuOpen(false);
    }
  }, [isMobile]);

  // Lock body scroll when mobile menu open
  useEffect(() => {
    if (isMobileMenuOpen) {
      lockScroll();
      return () => { unlockScroll(); };
    }
  }, [isMobileMenuOpen]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/');
    }
  };

  const handleSectionChange = (section: SidebarSection) => {
    setActiveSection(section);
    setIsMobileMenuOpen(false);
    if (isDetailViewOpen) {
      setIsDetailViewOpen(false);
    }
  };

  return (
    <Container>
      {/* Header with Logo, Theme Toggle */}
      <Header>
        <HeaderLeft>
          <HamburgerButton
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={t('nav.toggleMenu')}
            aria-expanded={isMobileMenuOpen}
          >
            <HamburgerLine open={isMobileMenuOpen} />
            <HamburgerLine open={isMobileMenuOpen} />
            <HamburgerLine open={isMobileMenuOpen} />
          </HamburgerButton>
          <LogoText>didi</LogoText>
        </HeaderLeft>
        <HeaderRight>
          <ThemeToggle />
          <UserProfileWidget onNavigateToSettings={() => { setActiveSection('settings'); setIsMobileMenuOpen(false); }} />
        </HeaderRight>
      </Header>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && isMobile && (
        <Overlay onClick={() => setIsMobileMenuOpen(false)} />
      )}

      {/* Sidebar Navigation */}
      <Sidebar
        activeSection={activeSection}
        onSectionChange={handleSectionChange}
        onLogout={handleLogout}
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
      />

      {/* Main Content Area */}
      <ContentArea hasSidebar={!isDetailViewOpen && !isMobile}>
        {activeSection === 'pipeline' && (
          <div role="tabpanel" id="pipeline-panel" aria-labelledby="pipeline-tab">
            <PipelineAnalysis />
          </div>
        )}

        {activeSection === 'agent' && (
          <div role="tabpanel" id="agent-panel" aria-labelledby="agent-tab">
            <AgentMode />
          </div>
        )}

        {activeSection === 'didi' && (
          <div role="tabpanel" id="didi-panel" aria-labelledby="didi-tab">
            <ManipulationTechniques />
          </div>
        )}

        {activeSection === 'source' && (
          <div role="tabpanel" id="source-panel" aria-labelledby="source-tab">
            <DomainAnalysis />
          </div>
        )}

        {activeSection === 'claim' && (
          <div role="tabpanel" id="claim-panel" aria-labelledby="claim-tab">
            <ClaimAnalysis />
          </div>
        )}

        {activeSection === 'aitamper' && (
          <div role="tabpanel" id="aitamper-panel" aria-labelledby="aitamper-tab">
            <AiTamper />
          </div>
        )}

        {activeSection === 'history' && (
          <div role="tabpanel" id="history-panel" aria-labelledby="history-tab">
            <History onDetailViewChange={setIsDetailViewOpen} />
          </div>
        )}

        {activeSection === 'settings' && (
          <div role="tabpanel" id="settings-panel" aria-labelledby="settings-tab">
            <Settings />
          </div>
        )}
      </ContentArea>

      {/* Background Effects */}
      <BackgroundGrid />
    </Container>
  );
};

export default Dashboard;

// Styled Components
const Container = styled.div`
  min-height: 100vh;
  background: var(--color-background, #050510);
  position: relative;
  overflow: hidden;
  transition: background-color 0.3s ease;
`;

const Header = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;
  background: rgba(5, 5, 16, 0.95);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(139, 92, 246, 0.1);
  padding: ${spacing.lg}px ${spacing['3xl']}px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.3s ease;

  [data-theme="light"] & {
    background: rgba(255, 255, 255, 0.95);
    border-bottom-color: rgba(139, 92, 246, 0.1);
  }

  @media (max-width: 768px) {
    padding: ${spacing.md}px ${spacing.lg}px;
  }
`;

const HamburgerButton = styled.button`
  display: none;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
  width: 44px;
  height: 44px;
  padding: 10px;
  background: transparent;
  border: none;
  cursor: pointer;
  z-index: 11;

  ${media.maxTablet} {
    display: flex;
  }
`;

const HamburgerLine = styled.span<{ open: boolean }>`
  display: block;
  width: 24px;
  height: 2px;
  background: #ffffff;
  border-radius: 2px;
  transition: all 0.3s ease;

  [data-theme="light"] & {
    background: ${colors.slateCharcoal};
  }

  &:nth-of-type(1) {
    transform: ${p => p.open ? 'rotate(45deg) translate(5px, 5px)' : 'none'};
  }
  &:nth-of-type(2) {
    opacity: ${p => p.open ? 0 : 1};
  }
  &:nth-of-type(3) {
    transform: ${p => p.open ? 'rotate(-45deg) translate(5px, -5px)' : 'none'};
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 7;
  backdrop-filter: blur(2px);
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.md}px;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.md}px;
`;

const LogoText = styled.h1`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize['2xl']};
  font-weight: ${typography.fontWeight.bold};
  color: #ffffff;
  margin: 0;
  text-transform: lowercase;
  transition: color 0.3s ease;

  [data-theme="light"] & {
    color: ${colors.slateCharcoal};
  }
`;

const ContentArea = styled.main<{ hasSidebar?: boolean }>`
  margin-top: 80px;
  margin-left: ${props => props.hasSidebar ? '280px' : '0'};
  padding: ${spacing['3xl']}px ${spacing['3xl']}px;
  min-height: calc(100vh - 80px);
  position: relative;
  z-index: 2;
  overflow-x: hidden;
  transition: margin-left 0.3s ease;

  ${media.maxTablet} {
    margin-top: 80px;
    margin-left: 0;
    padding: ${spacing.lg}px ${spacing.md}px;
    min-height: calc(100vh - 80px);
  }
`;

const BackgroundGrid = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image:
    linear-gradient(rgba(139, 92, 246, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(139, 92, 246, 0.03) 1px, transparent 1px);
  background-size: 60px 60px;
  z-index: 0;
  pointer-events: none;
  transition: opacity 0.3s ease;
  mask-image: radial-gradient(ellipse 80% 50% at 50% 0%, black 70%, transparent 100%);

  [data-theme="light"] & {
    background-image:
      linear-gradient(rgba(139, 92, 246, 0.05) 1px, transparent 1px),
      linear-gradient(90deg, rgba(139, 92, 246, 0.05) 1px, transparent 1px);
    mask-image: none;
  }
`;
